import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Bot, RefreshCw, Loader2, Boxes, Plug, Trash2, Plus, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Link } from "react-router-dom";

const ROLES = ["Owner", "Admin", "Developer", "Operator", "Analyst", "Viewer"];
const PERMS = {
  Owner: "Everything, including workspace management",
  Admin: "Everything except workspace management",
  Developer: "Create/edit bots, commands, webhooks, messages, automations",
  Operator: "Send messages, manage webhooks, view analytics/logs",
  Analyst: "View analytics and logs",
  Viewer: "Read-only",
};

/* Operational surface for the Manager Bot ingestion path. Without a registered
   webhook Telegram never delivers managed_bot updates, so this has to be
   visible and fixable rather than buried in the environment. */
function ManagerBotPanel({ canManage }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const load = useCallback(
    () => api.get("/manager/status").then((r) => setStatus(r.data)).catch(() => setStatus(false)),
    []
  );
  useEffect(() => { load(); }, [load]);

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/manager/webhook/register");
      toast.success("Manager Bot webhook registered", { description: data.url });
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const { data } = await api.post("/manager/test");
      setTest(data);
      toast.success(`Connected as @${data.username}`,
        { description: data.username_matches ? "Token matches the configured Manager Bot." : "Warning: token is for a different bot." });
    } catch (e) { setTest({ ok: false, error: apiErr(e) }); toast.error(apiErr(e)); }
    setTesting(false);
  };

  if (status === false) return null;
  if (!status) return <Skeleton className="h-40 w-full max-w-3xl" />;

  const registered = status.registered_url && status.registered_url === status.webhook_url;
  const Row = ({ k, v, tone = "" }) => (
    <div className="flex items-start justify-between py-2 border-b border-border/50 text-sm gap-4">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className={`mono text-xs text-right break-all ${tone}`}>{v}</span>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-3xl" data-testid="manager-bot-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bot size={18} className="text-primary" />
        <h3 className="font-semibold">Manager Bot</h3>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${registered
          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
          : "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"}`}
          data-testid="manager-webhook-state">
          {registered ? "Receiving updates" : "Not receiving updates"}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Managed bots enter BotControl automatically through the Manager Bot&rsquo;s
        <span className="mono text-xs"> managed_bot </span>
        update — the token is fetched server-side and never typed by anyone.
      </p>
      <Row k="Manager bot" v={status.manager_username ? `@${status.manager_username}` : "not configured"}
        tone={status.manager_configured ? "" : "text-amber-500"} />
      <Row k="Status" v={status.manager_configured ? "Connected" : "Not configured (set MANAGER_BOT_TOKEN)"}
        tone={status.manager_configured ? "text-emerald-500" : "text-amber-500"} />
      <Row k="Bot Management Mode"
        v={test?.management_mode === "verified" ? "Enabled" : status.manager_configured ? "Cannot verify until first bot" : "—"}
        tone={test?.management_mode === "verified" ? "text-emerald-500" : "text-muted-foreground"} />
      <Row k="Webhook secret" v={status.webhook_secret_configured ? "configured" : "missing"}
        tone={status.webhook_secret_configured ? "" : "text-rose-500"} />
      <Row k="Callback URL" v={status.webhook_url || status.config_error || "—"} />
      <Row k="Registered at Telegram" v={status.registered_url || "none"} />
      <Row k="Pending updates" v={status.pending_update_count ?? "—"} />
      {status.last_error_message && <Row k="Last error" v={status.last_error_message} tone="text-rose-500" />}
      <Row k="Unclaimed managed bots" v={status.unclaimed_managed_bots ?? 0}
        tone={status.unclaimed_managed_bots ? "text-amber-500" : ""} />
      {canManage && (
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={runTest}
            disabled={testing || !status.manager_configured} data-testid="btn-test-manager">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Test Connection
          </Button>
          <Button size="sm" className="gap-1.5" onClick={register}
            disabled={busy || !status.manager_configured || !status.webhook_secret_configured}
            data-testid="btn-register-manager-webhook">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {registered ? "Re-register webhook" : "Register webhook"}
          </Button>
        </div>
      )}
      {test && (
        <p className={`text-xs mt-2 ${test.ok ? (test.username_matches ? "text-emerald-500" : "text-amber-500") : "text-rose-500"}`} data-testid="manager-test-result">
          {test.ok ? `Connected as @${test.username}${test.username_matches ? "" : " — does not match the configured username!"}` : test.error}
        </p>
      )}
      {status.unclaimed_managed_bots > 0 && (
        <p className="text-xs text-amber-500 mt-2">
          {status.unclaimed_managed_bots} managed bot(s) arrived before their owner&rsquo;s Telegram
          account was connected. They are adopted automatically on the next account sync.
        </p>
      )}
    </div>
  );
}


/* Federated execution backends (BotNexus). Phase 1: read-only — BotControl reads
   the bots BotNexus hosts and shows them here; it never runs their code. */
/* Admin-only readiness checklist. Once every item is green ("AUTOMATION READY"),
   ordinary users never see infrastructure setup notices. */
const CHECK_LABELS = [
  ["api_credentials", "Telegram API ID / Hash"],
  ["manager_bot", "Manager Bot configured"],
  ["encryption_key", "Encryption key"],
  ["database", "Database connected"],
  ["public_https", "Public HTTPS backend"],
  ["webhook_registered", "Manager webhook registered"],
  ["webhook_healthy", "Manager webhook healthy"],
];

function SetupChecklist() {
  const [data, setData] = useState(null);
  const load = useCallback(
    () => api.get("/platform/setup-status").then((r) => setData(r.data)).catch(() => setData(false)), []);
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["job.updated", "bot.provisioned", "account.updated"], () => load());
  if (data === false) return null;
  if (!data) return <Skeleton className="h-40 w-full max-w-3xl" />;
  const ready = data.overall === "AUTOMATION_READY";
  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-3xl" data-testid="setup-checklist">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><h3 className="font-semibold">Platform automation</h3></div>
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${ready
          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
          : "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"}`} data-testid="automation-verdict">
          {ready ? "AUTOMATION READY" : "SETUP REQUIRED"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6">
        {CHECK_LABELS.map(([k, label]) => {
          const ok = data.checks[k];
          return (
            <div key={k} className="flex items-center gap-2 py-1.5 text-sm">
              {ok ? <CheckCircle2 size={15} className="text-emerald-500 shrink-0" /> : <AlertCircle size={15} className="text-amber-500 shrink-0" />}
              <span className={ok ? "" : "text-muted-foreground"}>{label}</span>
            </div>
          );
        })}
        <div className="flex items-center gap-2 py-1.5 text-sm">
          {data.management_mode === "verified" ? <CheckCircle2 size={15} className="text-emerald-500 shrink-0" /> : <Info size={15} className="text-muted-foreground shrink-0" />}
          <span className={data.management_mode === "verified" ? "" : "text-muted-foreground"}>
            Bot Management Mode {data.management_mode === "verified" ? "verified" : "(verifies on first bot)"}
          </span>
        </div>
      </div>
      {!ready && (
        <p className="text-xs text-muted-foreground mt-3">
          Complete the amber items to enable one-click bot creation.
          {!data.checks.manager_bot && " Set MANAGER_BOT_TOKEN in the backend environment."}
          {data.checks.manager_bot && !data.checks.webhook_registered && " Then register the Manager Bot webhook below."}
        </p>
      )}
    </div>
  );
}

function ExecutionBackendsPanel({ canManage }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", base_url: "", username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () => api.get("/federation/backends").then((r) => setItems(r.data.items)).catch(() => setItems([])),
    []
  );
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["federation.synced", "federation.updated"], () => load());

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/federation/backends", form);
      toast.success("BotNexus connected", { description: `${data.bots_visible} hosted bot(s) found — syncing.` });
      setForm({ name: "", base_url: "", username: "", password: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const sync = async (id) => { try { await api.post(`/federation/backends/${id}/sync`); toast.success("Sync started"); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (id) => { try { await api.delete(`/federation/backends/${id}`); toast.success("Backend removed"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="bg-card border border-border rounded-lg p-5 max-w-3xl" data-testid="execution-backends-panel">
      <div className="flex items-center gap-2 mb-1">
        <Boxes size={18} className="text-primary" />
        <h3 className="font-semibold">Execution backends · BotNexus</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Federate a BotNexus instance to see the bots it hosts here with live status. Read-only:
        BotControl never runs their code and never receives their tokens or sessions.
      </p>

      {canManage && !open && (
        <Button size="sm" variant="outline" className="gap-1.5 mb-3" onClick={() => setOpen(true)} data-testid="btn-add-backend">
          <Plus size={14} /> Connect a BotNexus instance
        </Button>
      )}
      {canManage && open && (
        <div className="border border-border rounded-lg p-4 mb-3 space-y-2.5 bg-secondary/30">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <div><Label>Name</Label><Input data-testid="input-backend-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Prod BotNexus" /></div>
            <div><Label>Base URL</Label><Input data-testid="input-backend-url" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="http://127.0.0.1:8100" /></div>
            <div><Label>Admin username</Label><Input data-testid="input-backend-user" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="admin" /></div>
            <div><Label>Admin password</Label><Input type="password" data-testid="input-backend-pass" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Credentials are validated with a live login, then stored only as encrypted ciphertext.</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={register} disabled={busy || !form.base_url || !form.username || !form.password} className="gap-1.5" data-testid="btn-save-backend">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />} Connect &amp; sync
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {!items ? <Skeleton className="h-16 w-full" /> : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">No execution backends connected.</div>
      ) : (
        <div className="space-y-2" data-testid="backend-list">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between p-3 rounded border border-border bg-secondary/20" data-testid={`backend-${b.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {b.name}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.status === "CONNECTED"
                    ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
                    : "text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/20"}`}>{b.status}</span>
                </div>
                <div className="text-xs text-muted-foreground mono truncate">{b.base_url}</div>
                <div className="text-xs text-muted-foreground">
                  {b.bots_synced != null ? `${b.bots_synced} hosted bot(s)` : "not synced yet"}
                  {b.last_synced_at ? ` · synced ${new Date(b.last_synced_at).toLocaleTimeString()}` : ""}
                  {b.last_error ? ` · ${b.last_error}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => sync(b.id)} data-testid={`btn-sync-backend-${b.id}`} title="Sync now"><RefreshCw size={14} /></Button>
                {canManage && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => remove(b.id)} data-testid={`btn-remove-backend-${b.id}`} title="Remove"><Trash2 size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Viewer");
  const load = () => api.get("/workspaces/members").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/workspaces/members", { email, role }); toast.success("Member added / updated"); setEmail(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const canManage = user?.role === "Owner";
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" desc="Workspace members, roles and access control." testId="settings-header" />
      <Button variant="outline" asChild><Link to="/ai-media/settings">AI Providers</Link></Button>

      <div className="bg-card border border-border rounded-lg p-5 max-w-2xl">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-primary" /><h3 className="font-semibold">Your account</h3></div>
        <div className="text-sm text-muted-foreground">{user?.name} · {user?.email}</div>
        <div className="text-xs mt-1"><span className="uppercase text-primary font-medium">{user?.role}</span> — {PERMS[user?.role]}</div>
      </div>

      {canManage && <SetupChecklist />}

      <ManagerBotPanel canManage={canManage} />

      <ExecutionBackendsPanel canManage={canManage} />

      <div className="bg-card border border-border rounded-lg overflow-hidden max-w-3xl">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between"><h3 className="font-semibold">Team &amp; RBAC</h3></div>
        {canManage && (
          <div className="p-5 flex flex-wrap items-end gap-2 border-b border-border">
            <div className="flex-1 min-w-[200px]"><Label>Invite by email</Label><Input data-testid="input-member-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" /></div>
            <div><Label>Role</Label><Select value={role} onValueChange={setRole}><SelectTrigger className="w-[140px]" data-testid="select-member-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={add} disabled={!email} className="gap-1.5" data-testid="btn-add-member"><UserPlus size={15} /> Add</Button>
          </div>
        )}
        {!members ? <Skeleton className="h-24 w-full m-4" /> : (
          <div className="divide-y divide-border">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between px-5 py-3" data-testid={`member-${m.user_id}`}>
                <div><div className="text-sm font-medium">{m.name || m.email}</div><div className="text-xs text-muted-foreground">{m.email}</div></div>
                <span className="text-xs uppercase font-medium text-primary">{m.role}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-5 max-w-3xl">
        <h3 className="font-semibold mb-3">Role permissions</h3>
        <div className="space-y-2">{ROLES.map((r) => (
          <div key={r} className="flex items-start justify-between py-2 border-b border-border/50 text-sm">
            <span className="font-medium w-24">{r}</span><span className="text-muted-foreground text-xs flex-1">{PERMS[r]}</span>
          </div>))}</div>
      </div>
    </div>
  );
}
